import type { MoveRisk, ProjectedStatus, DriverWithMoves } from './types';
import type { RouteEtaData } from './RouteEta';
import { computeRouteCascade, orderProjectionMoves, type RouteCascadeEstimate } from './routeEtaCascade';

export interface MoveProjection {
  status: ProjectedStatus;
  etaToOrigin: Date;
  etaToDestination: Date;
  isLinkedToPrev: boolean;
  isDueTomorrow: boolean;
}

const ACTIVE_STATUSES = new Set(['In Transit', 'At Origin', 'At Destination', 'Started']);

/** Pure cascading projection — identical math to DriverLane. `now` injectable for tests. */
export function computeMoveProjections(
  moves: MoveRisk[],
  now: Date = new Date(),
): Map<string, MoveProjection> {
  const dataMap = new Map<string, MoveProjection>();
  const todayStr = now.toISOString().split('T')[0];
  const sortedMoves = orderProjectionMoves(moves);

  let cumulativeMinutes = 0;
  const getDestNow = (move: MoveRisk): Date => {
    const tz = move.dest_tz_identifier;
    if (tz) {
      try {
        return new Date(now.toLocaleString('en-US', { timeZone: tz }));
      } catch { /* fall through */ }
    }
    return new Date(now.getTime());
  };

  sortedMoves.forEach((move, idx) => {
    const moveKey = `${move.pronumber}-${move.legnum}`;
    const prevMove = idx > 0 ? sortedMoves[idx - 1] : null;
    const isLinkedToPrev = !!(prevMove && prevMove.pronumber === move.pronumber && prevMove.destcode === move.origcode);
    const destNow = getDestNow(move);

    const etaToOrigin = isLinkedToPrev
      ? dataMap.get(`${prevMove!.pronumber}-${prevMove!.legnum}`)?.etaToDestination
        || new Date(destNow.getTime() + cumulativeMinutes * 60 * 1000)
      : new Date(destNow.getTime() + cumulativeMinutes * 60 * 1000);

    cumulativeMinutes += move.estimated_minutes || 0;
    const etaToDestination = new Date(destNow.getTime() + cumulativeMinutes * 60 * 1000);
    const projectedBuffer = (move.time_remaining_minutes ?? 0) - cumulativeMinutes;

    let deadlineHasPassed = false;
    const isAppointment = move.deadline_time?.startsWith('Appt:') || move.deadline_time?.startsWith('Pickup:');
    const effectiveDeadlineDate = move.deadline_date_raw || (isAppointment ? todayStr : '');
    if (move.deadline_time && effectiveDeadlineDate && effectiveDeadlineDate <= todayStr) {
      const timeMatch = move.deadline_time.match(/(\d{1,2}):(\d{2})/);
      if (timeMatch) {
        const deadlineToday = new Date(destNow);
        deadlineToday.setHours(parseInt(timeMatch[1], 10), parseInt(timeMatch[2], 10), 0, 0);
        deadlineHasPassed = destNow > deadlineToday;
      }
    }

    let status: ProjectedStatus;
    if (deadlineHasPassed || (move.time_remaining_minutes !== null && move.time_remaining_minutes < 0)) status = 'missed';
    else if (projectedBuffer < 0) status = 'will_miss';
    else if (projectedBuffer < 30) status = 'at_risk';
    else status = 'on_track';

    const isDueTomorrow = effectiveDeadlineDate ? effectiveDeadlineDate > todayStr : false;
    dataMap.set(moveKey, { status, etaToOrigin, etaToDestination, isLinkedToPrev, isDueTomorrow });

    if (idx < sortedMoves.length - 1) {
      const nextMove = sortedMoves[idx + 1];
      if (move.destcode !== nextMove.origcode) cumulativeMinutes += move.travel_to_next_minutes ?? 30;
    }
  });

  return dataMap;
}

export function getProjectedETA(move: MoveRisk, projections: Map<string, MoveProjection>): Date | null {
  const data = projections.get(`${move.pronumber}-${move.legnum}`);
  if (!data) return null;
  if (data.isLinkedToPrev) return data.etaToDestination;
  return move.status !== 'Dispatched' ? data.etaToDestination : data.etaToOrigin;
}

export function isETAToDestination(move: MoveRisk, projections: Map<string, MoveProjection>): boolean {
  const data = projections.get(`${move.pronumber}-${move.legnum}`);
  if (!data) return false;
  if (data.isLinkedToPrev) return true;
  return move.status !== 'Dispatched';
}

export function formatETA(eta: Date | null): string {
  if (!eta) return '';
  return eta.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}

export function isActiveStatus(status: string): boolean {
  return ACTIVE_STATUSES.has(status);
}

export interface LegEta {
  moveStatus: string;
  liveShareUrl?: string;
  liveShareExpiresAt?: string;
  routeEta?: RouteEtaData | null;
  routeCascade?: RouteCascadeEstimate;
  eta: Date | null;
  status: ProjectedStatus;
  isToDestination: boolean;
  notStarted: boolean; // driver hasn't started the day → show "Not started"
  /** Projected arrival at the leg's ORIGIN location. Null once the driver has
   *  reached it (any active status) — the leg's origin is then history. */
  etaToOrigin: Date | null;
  /** Projected arrival at the leg's DESTINATION location. */
  etaToDestination: Date | null;
}

/** Pure: assemble per-leg ETA for one PRO from its assigned drivers' full-day moves. */
export function buildProEta(
  pronumber: number,
  driversByUnit: Map<string, DriverWithMoves>,
  now: Date = new Date(),
): { byLeg: Map<number, LegEta>; activeLegnum: number | null } {
  const byLeg = new Map<number, LegEta>();
  let activeLegnum: number | null = null;
  if (!Number.isFinite(pronumber)) return { byLeg, activeLegnum };

  for (const driver of driversByUnit.values()) {
    if (!driver?.moves?.length) continue;
    const projections = computeMoveProjections(driver.moves, now);
    const routeCascade = computeRouteCascade(driver.moves, now);
    const driverHasStarted = driver.moves.some((m) => m.status !== 'Dispatched');
    // Exclude Completed: computeMoveProjections already drops them, so keeping
    // them here would surface a finished leg as a blank on_track byLeg entry —
    // DSM gates every projected prop on status !== 'Completed' (DriverLane.tsx).
    for (const move of driver.moves.filter((m) => m.pronumber === pronumber && m.status !== 'Completed')) {
      const proj = projections.get(`${move.pronumber}-${move.legnum}`);
      // Per-location split. `eta`/`isToDestination` keep DSM's single-value
      // semantics (header pill); etaToOrigin/etaToDestination let the caller
      // label BOTH of the leg's locations. Origin is suppressed once the move
      // is active — the driver is at/past that stop, so a projection there is
      // meaningless. Linked-to-prev moves still get one (it equals the previous
      // leg's destination arrival, i.e. the same physical place).
      byLeg.set(move.legnum, {
        moveStatus: move.status,
        liveShareUrl: move.live_share_url,
        liveShareExpiresAt: move.live_share_expires_at,
        routeEta: move.route_eta,
        routeCascade: routeCascade.get(`${move.pronumber}-${move.legnum}`),
        eta: driverHasStarted ? getProjectedETA(move, projections) : null,
        status: proj?.status ?? 'on_track',
        isToDestination: isETAToDestination(move, projections),
        notStarted: !driverHasStarted,
        etaToOrigin:
          driverHasStarted && move.status === 'Dispatched' ? proj?.etaToOrigin ?? null : null,
        etaToDestination: driverHasStarted ? proj?.etaToDestination ?? null : null,
      });
      // First active leg in driver-iteration then move-array order (a PRO has
      // ≤1 active leg in practice, so this tie-break is well-defined).
      if (driverHasStarted && activeLegnum === null && isActiveStatus(move.status)) {
        activeLegnum = move.legnum;
      }
    }
  }
  return { byLeg, activeLegnum };
}
